'use client';

import React from 'react';
import styled from 'styled-components';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useTranslation } from '@/app/components/translations/Translation';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { EvoWhatsAround, useHasWhatsAround } from './EvoWhatsAround';
import { EvoLocationMap, EvoLocationAddress } from './EvoLocationMap';

// Anchor id for the "See all" link in the What's-around block to smooth-scroll to.
export const EVO_SURROUNDINGS_SECTION_ID = 'evo-property-surroundings';

// The modal header lives outside the scrolling body (Dialog pins it above), so scrolling here
// already clears it — this is just a small breathing gap above the title after the smooth scroll.
const SCROLL_TARGET_OFFSET = 24;

// Desktop map dimensions (Figma node 979:25819): full-width, 256px tall, 8px corners.
const MAP_WIDTH = 976;
const MAP_HEIGHT = 256;

// Section content: the What's-nearby copy, then the map block, separated by the Figma space400 gap
// (node 979:25727). EvoWhatsAround self-hides when the hotel has no distances, so the gap only
// appears when both blocks are present.
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space400};
`;

// Map then address, 8px apart (Figma node 979:25818).
const MapBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Whether EvoPropertySurroundings will render anything — lets the modal skip the preceding divider
// and the "See all" scroll target when there's nothing to show.
export function useHasSurroundings() {
  const { data: staticData } = useFetchHotelStatic();
  return !!staticData?.coordinates;
}

export function EvoPropertySurroundings() {
  const { data: staticData } = useFetchHotelStatic();
  const { t } = useTranslation();
  const hasWhatsAround = useHasWhatsAround();

  if (!staticData?.coordinates) return null;

  return (
    <HotelPageContentSection
      className="modal-flush-section"
      id={EVO_SURROUNDINGS_SECTION_ID}
      style={{ scrollMarginTop: `${SCROLL_TARGET_OFFSET}px` }}
      title={t('vacation.select.cc.hotel_info.surroundings.title')}
    >
      <Body>
        {hasWhatsAround && <EvoWhatsAround hideSeeAll={true} hideIcons={true} />}
        <MapBlock>
          <EvoLocationMap width={MAP_WIDTH} height={MAP_HEIGHT} borderRadius={8} />
          <EvoLocationAddress />
        </MapBlock>
      </Body>
    </HotelPageContentSection>
  );
}
